import { AfterViewInit, Component, ElementRef, forwardRef, HostBinding, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { JemsCalendarHeaderComponent } from './jems-calendar-header.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TextInputComponent } from '../text-input/text-input.component';
import { DateTimePickerHandler } from 'src/app/class/date-time-picker-handler';
import * as dayjs from 'dayjs';
import { DomService } from 'src/app/service/dom.service';
import { TimezoneService } from 'src/app/service/timezone.service';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TimeZoneInputComponent } from '../time-zone-input/time-zone-input.component';
import * as _ from 'lodash';

@Component({
  selector: 'app-datetimepicker-input',
  templateUrl: './datetimepicker-input.component.html',
  styleUrls: ['./datetimepicker-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatetimepickerInputComponent),
    multi: true
  }]
})
export class DatetimepickerInputComponent extends TextInputComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('datepicker', { static: false}) datepicker;

  /** Header custom pro mini-calendário — bypassa o `.toLocaleUpperCase()`
   *  hardcoded do NgxMatCalendarHeader default da lib no rótulo do período. */
  public jemsCalendarHeader = JemsCalendarHeaderComponent;
  @ViewChild(TimeZoneInputComponent) timeZoneInput: TimeZoneInputComponent;
  @Input() max: number;
  @Input() min: number;
  @Input() eventTimeZone: string;
  @Input() enabledTimeZone: boolean;
  @Input() timeZoneController: string;
  @Input() disabledTime: boolean = false;
  @Input() datePipe: string = 'dateTime';
  /**
   * Quando true (DEFAULT atual), o overlay do calendário NÃO fecha por clique
   * no backdrop nem por ESC — só pelos botões de cancelar (X) e confirmar (check).
   * Modal-by-default a partir do JEMS3 (decisão UX); consumidores podem passar
   * `[lockClose]="false"` pra restaurar o dismissível.
   */
  @Input() lockClose: boolean = true;
  /**
   * Quando true (e enabledTimeZone), exibe no próprio campo (à direita) um globo
   * sutil que abre o VISUALIZADOR de fusos (app-dialog-timezone, read-only): mostra
   * a data/hora do valor em cada fuso. Não é o seletor — a escolha de fuso continua
   * no calendário (app-time-zone-input movido pro overlay).
   */
  @Input() inlineTimeZone: boolean = false;
  /**
   * Texto descritivo do campo. Quando setado, renderiza um rótulo notch'ado
   * na borda superior da caixa de texto (estilo Material outline). Útil pra
   * preservar o "descritivo de campo" no preset `filter-styled` (onde o
   * `<mat-label>` nativo fica oculto). Aceita chave i18n — é passado pelo
   * pipe translate automaticamente.
   */
  @Input() description: string;
  /**
   * Largura do host. Quando setada, aplica `width` no `:host` via
   * HostBinding (sobrepõe a default do SCSS — `width: auto` no preset
   * filter-styled, que assume largura do conteúdo). Aceita qualquer valor
   * CSS válido (`'200px'`, `'100%'`, `'16rem'`, etc.).
   */
  @Input() @HostBinding('style.width') width: string;
  defaultTime: Array<number>= [23,59,0];

  @Input() link: {
    label: string,
    route: string,
    align: 'start' | 'end'
  };

  public DateTimePickerHandler: DateTimePickerHandler;
  public container: Element;
  public clonedForm: FormGroup;

  @ViewChild('dateTimePicker', { static: false }) containerDateTimePicker: ElementRef;

  constructor(
    private domService: DomService,
    private tzService: TimezoneService
  ) {
    super();
    this.DateTimePickerHandler = new DateTimePickerHandler(this.tzService, this.domService);
  }

  ngOnInit() {
    this.listenClosePicker();
    this.clonedForm = this.cloneForm();
    this.clonedForm.value[this.name]=new Date(2024, 8, 10)
  }

  /** Re-cloneForm quando o `formGroupController` é REASSINADO pelo pai
   *  (ex.: `discardChanges()` na page de event-settings reinicializa o
   *  form inteiro com `this.fb.group({...})`). Sem isso, o `clonedForm`
   *  fica preso ao FormGroup antigo e o input segue mostrando o valor
   *  pré-discard. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formGroupController && !changes.formGroupController.firstChange && this.formGroupController) {
      this.clonedForm = this.cloneForm();
    }
  }

  ngAfterViewInit(): void {
    this.DateTimePickerHandler.removeInputDateTime(this.containerDateTimePicker, '._container-input', '.container-date-time-mask');
  }

  private cloneForm() {
    return _.cloneDeep(this.formGroupController);
  }
  
  /** Estado da sessão atual do picker (snapshot, flag de interação,
   *  subscription do watcher). Consolidado num objeto pra facilitar
   *  o reset e centralizar a lógica em `resetPickerState()`. */
  private pickerSession: {
    initialValue: any;
    /** Snapshot em STRING do initialValue — imune à mutação do Date pelo
     *  timepicker interno (que faz `setHour` no mesmo objeto referenciado). */
    initialValueStr: string;
    initialTimeZone: string | null;
    interacted: boolean;
    sub?: Subscription;
    tzSub?: Subscription;
  } = { initialValue: null, initialValueStr: 'null', initialTimeZone: null, interacted: false };

  /** Sincroniza clonedForm ← formGroupController + reseta o estado
   *  interno (`_selected` / `_startAt`) do ngx-mat-datetime-picker
   *  com o valor commit-ado. Chamado ANTES de abrir o overlay, garante
   *  que o calendário não reabra mostrando uma seleção cancelada de
   *  sessão anterior. Retorna o snapshot do valor commit-ado. */
  private resetPickerState(): any {
    const committed = this.formGroupController?.get(this.name)?.value ?? null;
    this.clonedForm?.get(this.name)?.setValue(committed, { emitEvent: false });
    const picker = this.datepicker as any;
    if (picker) {
      // CLONE o Date antes de passar pro picker — o timepicker interno faz
      // `setHour` no OBJETO, mutando quem quer que compartilhe a referência.
      // Sem clonar, alterações de hora/minuto entram no formGroupController
      // "original" e o cancel (×) não consegue descartá-las.
      const clonedCommitted = committed instanceof Date
        ? new Date(committed.getTime())
        : (committed && typeof (committed as any).clone === 'function'
            ? (committed as any).clone()
            : committed);
      if(committed){
        picker._selected = clonedCommitted;
        picker._startAt = clonedCommitted;
      }
    }
    this.pickerSession.sub?.unsubscribe();
    const initialTimeZone = this.timeZoneController
      ? (this.formGroupController?.get(this.timeZoneController)?.value ?? null)
      : null;
    this.pickerSession = {
      initialValue: committed,
      initialValueStr: JSON.stringify(committed),
      initialTimeZone,
      interacted: false,
    };
    return committed;
  }

  /** Abre o overlay do calendário a partir de um clique em QUALQUER parte
   *  da caixa (input ou wrapper), não só no ícone de calendário. Roda o
   *  `generateInputInDateTime` antes (mesmo setup que o toggle dispara).
   *
   *  Toggle behavior: se o overlay já está aberto e NÃO há alteração
   *  (`isPickerDirty()` false), um novo clique na caixa fecha o picker
   *  (equivale a "cancelar sem perder nada"). Se há alteração, fica
   *  aberto — usuário precisa confirmar ou cancelar explicitamente. */
  public openPicker(event: Event): void {
    if (this.datepicker?.opened) {
      if (!this.isPickerDirty()) { this.datepicker.close(); }
      return;
    }
    this.resetPickerState();
    this.generateInputInDateTime(event as PointerEvent);
    this.datepicker?.open();
    // Watcher de mudança real no clonedForm — ngx-mat-datetime-picker
    // commits o valor selecionado ao clonedForm conforme o usuário
    // interage no calendário. Qualquer divergência do snapshot marca o
    // picker como sujo.
    const ctrl = this.clonedForm?.get(this.name);
    if (ctrl) {
      this.pickerSession.sub = ctrl.valueChanges.subscribe(v => {
        if (JSON.stringify(v) !== this.pickerSession.initialValueStr) {
          this.pickerSession.interacted = true;
        }
      });
    }
    // Assina a mudança de fuso — só marca dirty se o fuso escolhido for
    // DIFERENTE do inicial (escolher o MESMO fuso não conta).
    this.pickerSession.tzSub = this.DateTimePickerHandler.timeZoneChanged$.subscribe((tz: string) => {
      if (tz === this.pickerSession.initialTimeZone) { return; }
      this.pickerSession.interacted = true;
      const overlayEl: HTMLElement | undefined = (this.datepicker as any)?._popupRef?.overlayElement;
      overlayEl?.classList.remove('is-clean-picker');
    });
  }

  /** Bindado em `(closed)` no template. Limpa watcher, re-sincroniza o
   *  clonedForm (cobre Cancel) e marca dirty quando o valor mudou
   *  efetivamente (caso CONFIRM — `setValue` do handler não marca dirty
   *  sozinho, precisamos sinalizar pra floating-submit-bar). */
  public onCalendarClosed(): void {
    this.pickerSession.sub?.unsubscribe();
    this.pickerSession.tzSub?.unsubscribe();
    const initialStr = this.pickerSession.initialValueStr;
    const ctrl = this.formGroupController?.get(this.name);
    const committed = ctrl?.value ?? null;
    this.clonedForm?.get(this.name)?.setValue(committed, { emitEvent: false });
    if (JSON.stringify(committed) !== initialStr) {
      ctrl?.markAsDirty();
    }
    this.pickerSession = { initialValue: null, initialValueStr: 'null', initialTimeZone: null, interacted: false };
  }

  /** True se houve interação dentro do calendário OU se o valor do form
   *  cloned já mudou desde o snapshot (cobre eventuais writes early). */
  private isPickerDirty(): boolean {
    if (this.pickerSession.interacted) { return true; }
    const current = this.clonedForm?.value?.[this.name] ?? null;
    return JSON.stringify(current) !== this.pickerSession.initialValueStr;
  }

  /** Copia a seleção atual do picker (mesmo antes do Confirm) pro clonedForm,
   *  fazendo a caixa de texto refletir o que o usuário está mexendo em
   *  tempo real. Ao cancelar (×), `onCalendarClosed` restaura o valor original. */
  private syncClonedFormFromPicker(): void {
    const picker: any = this.datepicker;
    if (!picker) { return; }
    // Fallback chain: `_selected` é o campo padrão do MatDatepickerBase (Material 11);
    // `_model.selection` cobre versões novas do ngx-mat-datetime-picker que trocaram
    // p/ selection model.
    const currentDate = picker._selected ?? picker._model?.selection ?? null;
    if (currentDate == null) { return; }
    const ctrl = this.clonedForm?.get(this.name);
    if (!ctrl) { return; }
    const previous = ctrl.value;
    if (JSON.stringify(previous) !== JSON.stringify(currentDate)) {
      ctrl.setValue(currentDate);
    }
  }

  public listenClosePicker() {
    this.domService.closeCalendar$.subscribe(isClose => {
      if (isClose) {
        this.datepicker?.close();
      }
    });
  }
  
  /**
   * Chamado quando o overlay do calendário abre (`(opened)` do ngx-mat-datetime-picker).
   * Com `lockClose`, torna o overlay "modal": intercepta — em fase de CAPTURA, antes do
   * CDK/da lib (que chamam `cancel()`) — o clique no backdrop e a tecla ESC, de modo que
   * só os botões X (cancelar) e check (confirmar) fecham o calendário. Os botões seguem
   * intactos porque o bloqueio é nos EVENTOS de dismiss, não no método `cancel()`.
   */
  public onCalendarOpened(): void {
    if (!this.lockClose) { return; }
    const popupRef: any = this.datepicker && (this.datepicker as any)._popupRef;
    if (!popupRef) { return; }

    const backdrop: HTMLElement = popupRef.backdropElement;
    if (backdrop && backdrop.getAttribute('data-lock-close') !== '1') {
      backdrop.setAttribute('data-lock-close', '1');
      backdrop.addEventListener('click', (event: MouseEvent) => {
        // Modal SÓ quando há mudanças. Sem dirty, click fora fecha normal.
        if (!this.isPickerDirty()) { return; }
        event.stopImmediatePropagation();
        event.preventDefault();
      }, true);
    }

    const overlayEl: HTMLElement = popupRef.overlayElement;
    if (overlayEl && overlayEl.getAttribute('data-lock-close') !== '1') {
      overlayEl.setAttribute('data-lock-close', '1');
      overlayEl.addEventListener('keydown', (event: KeyboardEvent) => {
        // ESC também só bloqueia quando há mudanças a preservar.
        if (this.isPickerDirty() && (event.key === 'Escape' || event.keyCode === 27)) {
          event.stopImmediatePropagation();
        }
      }, true);
      // Dirty por diff direto: compara `picker._selected` (atualizado em
      // qualquer mudança de dia/mês/ano/hora/minuto) com o valor inicial.
      // Simples, sem cadeia de valueChanges: se mudou, dirty; se não mudou
      // (clicou no mesmo dia/hora), fica limpo.
      // EXCEÇÃO: click no `.time-zone-button` só abre o popover do fuso;
      // a troca real de fuso vai por `timeZoneChanged$`.
      const checkDirty = () => setTimeout(() => {
        const picker: any = this.datepicker;
        const current = picker?._selected ?? null;
        // Compara contra o SNAPSHOT em string — o Date é mutado pelo
        // timepicker (setHour), então `initialValue` seria "atualizado"
        // junto e o diff nunca bateria.
        if (JSON.stringify(current) !== this.pickerSession.initialValueStr) {
          this.pickerSession.interacted = true;
          overlayEl.classList.remove('is-clean-picker');
        }
        this.syncClonedFormFromPicker();
      }, 0);
      overlayEl.addEventListener('click', (ev: MouseEvent) => {
        const target = ev.target as HTMLElement | null;
        if (target && target.closest('.time-zone-button')) { return; }
        checkDirty();
      }, true);
      // Cobre digitação direta nos campos hora/minuto (não gera click).
      overlayEl.addEventListener('input', () => checkDirty(), true);
    }

    // Estado inicial "limpo" — botões × e ✓ desabilitados via CSS até haver
    // interação. Setado SEMPRE na abertura (mesmo em reaberturas).
    if (overlayEl) { overlayEl.classList.add('is-clean-picker'); }
  }

  public get offsetTimeZoneSelected(): string {
    const timeZone = this.formGroupController.get(this.timeZoneController).value;
    const offset = this.tzService.getUTCTimeZone(timeZone);
    return offset ? `(UTC${offset})` : '';
  }

  dateToString(date: number) {
    return dayjs(date).format('DD/MM/YYYY, HH:mm:ss');
  }

  public get dateTimeViewed(): Date {
    return this.clonedForm.get(this.name).value;
  }

  public generateInputInDateTime($event: PointerEvent) {
    if (this.enabledTimeZone) {
      this.DateTimePickerHandler.generateInputInDateTime('.time-container', this.formGroupController, this.clonedForm, this.timeZoneController, this.name, this.getContainerTimeZoneInput($event), this.eventTimeZone);
      this.timeZoneInput.radioSelectedTimeZone = this.formGroupController.get(this.timeZoneController).value;
      return;
    }

    this.DateTimePickerHandler.addOnlyButtonClose('.actions', this.formGroupController, this.clonedForm, this.name);
  }

  private getContainerTimeZoneInput($event: PointerEvent) {
    if (this.container) return this.container;
    
    const containerDateTimePicker = ($event.target as HTMLElement).closest('app-datetimepicker-input');
    const appTimeZoneInput = containerDateTimePicker.getElementsByTagName('app-time-zone-input')[0];
    const containerButton = appTimeZoneInput.getElementsByClassName('container-button')[0];
    const div = appTimeZoneInput.getElementsByTagName('div')[0];
    this.container = document.createElement('div');
    this.container.appendChild(containerButton);
    this.container.appendChild(div);

    return this.container;
  }
}
